(() => {
  if (customElements.get('tb-gummy-motion')) return;
  const TAU = Math.PI * 2;
  const clamp = (x, a, b) => Math.min(b, Math.max(a, x));
  const mix = (a, b, t) => a.map((v, i) => v + (b[i] - v) * t);
  const normal = v => { const length = Math.hypot(...v) || 1; return v.map(n => n / length); };
  const signed = (n, power) => Math.sign(n) * Math.pow(Math.abs(n), power);
  const vertexShader = `
    attribute vec3 aPosition;
    attribute vec3 aNormal;
    uniform mat4 uModel;
    uniform mat4 uProjection;
    varying vec3 vPosition;
    varying vec3 vNormal;
    varying vec3 vLocal;
    void main() {
      vec4 world = uModel * vec4(aPosition, 1.0);
      vPosition = world.xyz;
      vNormal = normalize(mat3(uModel) * aNormal);
      vLocal = aPosition;
      gl_Position = uProjection * world;
    }
  `;
  const fragmentShader = `
    #ifdef GL_FRAGMENT_PRECISION_HIGH
    precision highp float;
    #else
    precision mediump float;
    #endif
    uniform vec3 uColor;
    uniform float uGloss;
    uniform float uStamp;
    varying vec3 vPosition;
    varying vec3 vNormal;
    varying vec3 vLocal;
    float box(vec2 p, vec2 b) { return max(abs(p.x)-b.x, abs(p.y)-b.y); }
    float mark(vec2 p) {
      float t = min(box(p-vec2(-.27,.22),vec2(.22,.055)),box(p-vec2(-.27,-.025),vec2(.055,.245)));
      float outer = min(length((p-vec2(.18,.13))*vec2(1.,1.1))-.18,length((p-vec2(.18,-.14))*vec2(1.,1.1))-.18);
      float inner = min(length((p-vec2(.17,.13))*vec2(1.,1.1))-.075,length((p-vec2(.17,-.14))*vec2(1.,1.1))-.075);
      float b = min(max(outer,-inner),box(p-vec2(.045,-.005),vec2(.05,.30)));
      return min(t,b);
    }
    void main() {
      vec3 grain = vec3(sin(vLocal.y*142.+vLocal.z*43.),sin(vLocal.z*137.+vLocal.x*37.),sin(vLocal.x*157.+vLocal.y*53.));
      vec3 N = normalize(vNormal + grain * .002 * uGloss);
      vec3 V = normalize(vec3(0.,0.,12.) - vPosition);
      vec3 L = normalize(vec3(-3.,4.,6.));
      vec3 R = normalize(vec3(4.,1.,3.));
      float diffuse = max(dot(N,L),0.);
      float fill = max(dot(N,R),0.);
      float spec = pow(max(dot(N,normalize(L+V)),0.),58.)*.78;
      spec += pow(max(dot(N,normalize(R+V)),0.),95.)*.60;
      float broad = pow(max(dot(N,normalize(vec3(-.4,1.2,2.4)+V)),0.),12.)*.18;
      float rim = pow(1.-max(dot(N,V),0.),2.5);
      vec3 color = uColor*(.47 + diffuse*.54 + fill*.14);
      color += (vec3(1.) - uColor*.35)*(spec*uGloss + broad);
      color += uColor*rim*.30 + vec3(1.,.88,.82)*rim*.09;
      float stamp = (1.-smoothstep(-.01,.008,mark(vLocal.xy)))*smoothstep(.34,.40,vLocal.z)*uStamp;
      color *= 1.-stamp*.27;
      color += vec3(.13)*stamp*max(dot(N,L),0.);
      gl_FragColor = vec4(clamp(color,0.,1.),1.);
    }
  `;
  function transform(x, y, z, rx, ry, rz, scale = 1) {
    const a = Math.cos(rx), b = Math.sin(rx), c = Math.cos(ry), d = Math.sin(ry), e = Math.cos(rz), f = Math.sin(rz);
    return new Float32Array([
      e*c*scale, f*c*scale, -d*scale, 0,
      (e*d*b-f*a)*scale, (f*d*b+e*a)*scale, c*b*scale, 0,
      (e*d*a+f*b)*scale, (f*d*a-e*b)*scale, c*a*scale, 0,
      x,y,z,1
    ]);
  }
  function surface(rx, ry, rz, exponent = 3.4, segments = 56, rows = 32) {
    const vertices = [], indices = [], power = 2 / exponent;
    for (let row = 0; row <= rows; row++) {
      const phi = -Math.PI/2 + Math.PI*row/rows;
      for (let col = 0; col <= segments; col++) {
        const theta = TAU*col/segments;
        const x = signed(Math.cos(phi),power)*signed(Math.cos(theta),power);
        const y = signed(Math.sin(phi),power);
        const z = signed(Math.cos(phi),power)*signed(Math.sin(theta),power);
        const n = normal([signed(x,exponent-1)/rx,signed(y,exponent-1)/ry,signed(z,exponent-1)/rz]);
        vertices.push(x*rx,y*ry,z*rz,...n);
      }
    }
    for (let row = 0; row < rows; row++) for (let col = 0; col < segments; col++) {
      const a = row*(segments+1)+col, b = a+segments+1;
      indices.push(a,b,a+1,a+1,b,b+1);
    }
    return { vertices, indices };
  }
  function torus(radius, tube, segments = 80, rows = 10) {
    const vertices = [], indices = [];
    for (let i=0;i<=segments;i++) for (let j=0;j<=rows;j++) {
      const u=TAU*i/segments,v=TAU*j/rows,c=Math.cos(u),s=Math.sin(u),cv=Math.cos(v),sv=Math.sin(v);
      vertices.push((radius+tube*cv)*c,(radius+tube*cv)*s,tube*sv,c*cv,s*cv,sv);
    }
    for(let i=0;i<segments;i++) for(let j=0;j<rows;j++) {
      const a=i*(rows+1)+j,b=a+rows+1;indices.push(a,b,a+1,a+1,b,b+1);
    }
    return {vertices,indices};
  }
  class Studio {
    constructor(canvas, palette) {
      const gl = canvas.getContext('webgl', { alpha:true, antialias:true, premultipliedAlpha:false, powerPreference:'low-power' });
      if (!gl) throw new Error('WebGL unavailable');
      this.gl=gl;this.palette=palette;this.canvas=canvas;this.resources=[];
      const compile=(type,source)=> {
        const shader=gl.createShader(type);gl.shaderSource(shader,source);gl.compileShader(shader);
        if(!gl.getShaderParameter(shader,gl.COMPILE_STATUS)) { const message=gl.getShaderInfoLog(shader);gl.deleteShader(shader);throw new Error(message); }
        return shader;
      };
      const vs=compile(gl.VERTEX_SHADER,vertexShader),fs=compile(gl.FRAGMENT_SHADER,fragmentShader);
      this.program=gl.createProgram();gl.attachShader(this.program,vs);gl.attachShader(this.program,fs);gl.linkProgram(this.program);
      gl.deleteShader(vs);gl.deleteShader(fs);
      if(!gl.getProgramParameter(this.program,gl.LINK_STATUS)) throw new Error('Shader link failed');
      gl.useProgram(this.program);
      this.position=gl.getAttribLocation(this.program,'aPosition');this.normal=gl.getAttribLocation(this.program,'aNormal');
      this.uniforms=Object.fromEntries(['Model','Projection','Color','Gloss','Stamp'].map(n=>[n,gl.getUniformLocation(this.program,'u'+n)]));
      this.gummy=this.mesh(surface(1.02,1.12,.43));
      this.layer=this.mesh(surface(1.14,.27,1.14,3.5));
      this.bead=this.mesh(surface(1,1,1,2,20,12));
      this.ring=this.mesh(torus(1.65,.022));
      this.halo=this.mesh(torus(2.25,.012));
      gl.enable(gl.DEPTH_TEST);gl.depthFunc(gl.LEQUAL);gl.clearColor(0,0,0,0);
    }
    mesh({vertices,indices}) {
      const gl=this.gl,vertex=gl.createBuffer(),index=gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER,vertex);gl.bufferData(gl.ARRAY_BUFFER,new Float32Array(vertices),gl.STATIC_DRAW);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER,index);gl.bufferData(gl.ELEMENT_ARRAY_BUFFER,new Uint16Array(indices),gl.STATIC_DRAW);
      const mesh={vertex,index,count:indices.length};this.resources.push(mesh);return mesh;
    }
    resize(width,height) {
      const ratio=Math.min(window.devicePixelRatio||1,width<750?1.5:1.25);
      this.canvas.width=Math.max(1,Math.round(width*ratio));this.canvas.height=Math.max(1,Math.round(height*ratio));
      this.aspect=width/Math.max(1,height);this.width=width;
      this.gl.viewport(0,0,this.canvas.width,this.canvas.height);
      this.gl.uniformMatrix4fv(this.uniforms.Projection,false,new Float32Array([1/(3*this.aspect),0,0,0,0,1/3,0,0,0,0,-.05,0,0,0,0,1]));
    }
    draw(mesh,matrix,color,gloss=1,stamp=0) {
      const gl=this.gl;
      gl.bindBuffer(gl.ARRAY_BUFFER,mesh.vertex);gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER,mesh.index);
      gl.enableVertexAttribArray(this.position);gl.vertexAttribPointer(this.position,3,gl.FLOAT,false,24,0);
      gl.enableVertexAttribArray(this.normal);gl.vertexAttribPointer(this.normal,3,gl.FLOAT,false,24,12);
      gl.uniformMatrix4fv(this.uniforms.Model,false,matrix);gl.uniform3fv(this.uniforms.Color,color);
      gl.uniform1f(this.uniforms.Gloss,gloss);gl.uniform1f(this.uniforms.Stamp,stamp);
      gl.drawElements(gl.TRIANGLES,mesh.count,gl.UNSIGNED_SHORT,0);
    }
    hero(time, tilt) {
      const p=this.palette,t=time*.18,desktop=this.width>=990;
      const center=desktop?this.aspect*1.12:0;
      const scale=desktop?1.57:1.19;
      this.draw(this.halo,transform(center,0,-1.6,.5,.1,t*.25,1.1),p.line,.45);
      const poses=[
        [center+.18,.05,0,.16+Math.sin(t)*.10,.30+Math.sin(t*.8)*.28,-.24+Math.sin(t*.7)*.11,scale],
        [center-1.75,1.65,-.65,-.24,Math.sin(t+2)*.42,.52+Math.sin(t)*.13,.66],
        [center+1.75,-1.68,.35,.28,Math.sin(t+4)*.40,.32+Math.cos(t)*.13,.70]
      ];
      poses.forEach((v,i)=>{
        v[1]+=Math.sin(t*1.5+i*2)*.16;v[3]+=tilt[1]*.10;v[4]+=tilt[0]*.15;
        this.draw(this.gummy,transform(...v),mix(p.berry,p.light,i===0?.0:.16),1,1);
      });
      for(let i=0;i<7;i++) {
        const angle=i*TAU/7+t*.26;
        this.draw(this.bead,transform(center+Math.cos(angle)*2.58,Math.sin(angle)*2.36,-.8,0,0,0,.035+(i%3)*.013),i%2?p.berry:p.line,.6);
      }
    }
    technology(time,stage,tilt) {
      const p=this.palette,t=time*.28;
      const spread=.45+(Math.sin(t)*.5+.5)*.57;
      const ry=.45+Math.sin(t*.5)*.18+tilt[0]*.12;
      const rx=.43+Math.sin(t*.4)*.08+tilt[1]*.08;
      this.draw(this.halo,transform(0,.15,-1.3,.35,.15,t*.15,.93),p.line,.4);
      if(stage===0) {
        for(let layer=-1;layer<=1;layer++) {
          this.draw(this.layer,transform(0,layer*spread,.0,rx,ry,-.15,1),mix(p.berry,p.light,(layer+1)*.19),1);
        }
        for(let i=0;i<12;i++) {
          const angle=i*TAU/12+t*.24;
          this.draw(this.bead,transform(Math.cos(angle)*1.67,Math.sin(angle)*1.30,Math.sin(angle)*.40,0,0,0,.075),p.light,1);
        }
      } else if(stage===1) {
        this.draw(this.bead,transform(0,0,.1,0,0,0,.66),p.light,.95);
        for(let j=0;j<3;j++) {
          this.draw(this.ring,transform(0,0,0,.65+j*.85,.35+j*.45,t*.16+j*.4,.70+j*.10),mix(p.berry,p.line,j*.20),.9);
          for(let i=0;i<6;i++) {
            const angle=i*TAU/6+t*(j%2?-.40:.40)+j;
            const radius=1.24+j*.17;
            this.draw(this.bead,transform(Math.cos(angle)*radius,Math.sin(angle)*radius*Math.cos(j*.65),Math.sin(angle)*radius*Math.sin(j*.65),0,0,0,.10+j*.026),p.berry,1);
          }
        }
      } else {
        for(let i=0;i<3;i++) {
          const angle=i*TAU/3-.5+t*.10;
          this.draw(this.gummy,transform(Math.cos(angle)*1.18,Math.sin(angle)*1.18,Math.sin(angle)*.35,.17+Math.sin(t+i)*.13,.18+Math.cos(t+i)*.23,angle*.14,.76),mix(p.berry,p.light,i*.10),1,1);
        }
      }
    }
    render(time,variant,stage,tilt) {
      this.gl.clear(this.gl.COLOR_BUFFER_BIT|this.gl.DEPTH_BUFFER_BIT);
      variant==='hero'?this.hero(time,tilt):this.technology(time,stage,tilt);
    }
    destroy() {
      this.resources.forEach(m=>{this.gl.deleteBuffer(m.vertex);this.gl.deleteBuffer(m.index);});
      this.gl.deleteProgram(this.program);
    }
  }
  function color(value) {
    const hex=value.trim().replace('#','');
    if(/^[\da-f]{6}$/i.test(hex)) return [0,2,4].map(i=>parseInt(hex.slice(i,i+2),16)/255);
    const channels=value.match(/[\d.]+/g);
    return channels?.length>=3?channels.slice(0,3).map(Number).map(v=>v/255):[.85,.22,.32];
  }
  customElements.define('tb-gummy-motion',class extends HTMLElement {
    connectedCallback() {
      this.abort=new AbortController();const options={signal:this.abort.signal};
      this.canvas=this.querySelector('canvas');this.button=this.querySelector('[data-motion-toggle]');
      this.motion=matchMedia('(prefers-reduced-motion: reduce)');
      this.variant=this.dataset.variant;this.inView=false;this.userPaused=false;this.userStarted=false;
      this.running=false;this.time=0;this.last=0;this.stage=0;this.tilt=[0,0];this.failed=false;
      this.button.addEventListener('click',()=>{
        if(this.running) this.userPaused=true;
        else {this.userPaused=false;this.userStarted=true;}
        this.sync();
      },options);
      this.addEventListener('pointermove',event=>{
        if(event.pointerType!=='mouse'||this.motion.matches)return;
        const rect=this.getBoundingClientRect();this.tilt=[(event.clientX-rect.left)/rect.width-.5,(event.clientY-rect.top)/rect.height-.5];
      },options);
      this.addEventListener('pointerleave',()=>{this.tilt=[0,0];},options);
      this.canvas.addEventListener('webglcontextlost',event=>{
        event.preventDefault();this.stop();this.studio=null;this.classList.remove('is-rendered');this.button.hidden=true;
      },options);
      this.canvas.addEventListener('webglcontextrestored',()=>{this.failed=false;this.sync();},options);
      document.addEventListener('visibilitychange',()=>this.sync(),options);
      document.addEventListener('tb:dialog-change',()=>this.sync(),options);
      this.motion.addEventListener('change',()=>{this.userStarted=false;this.sync();},options);
      navigator.connection?.addEventListener('change',()=>this.sync(),options);
      this.observer=new IntersectionObserver(entries=>{this.inView=entries[0].isIntersecting;this.sync();},{threshold:.08});
      this.observer.observe(this);
      // Also measure after scroll/resize: anchor navigation and embedded previews
      // can delay IntersectionObserver delivery on mobile browsers.
      const scheduleViewport=()=>{
        if(this.viewportFrame)return;
        this.viewportFrame=requestAnimationFrame(()=>{this.viewportFrame=0;this.checkViewport();});
      };
      window.addEventListener('scroll',scheduleViewport,{...options,passive:true});
      window.addEventListener('resize',scheduleViewport,{...options,passive:true});
      scheduleViewport();
      this.resizer=new ResizeObserver(()=>{
        if(this.studio){this.studio.resize(this.canvas.parentElement.clientWidth,this.canvas.parentElement.clientHeight);this.render();}
      });
      this.resizer.observe(this);
      this.querySelectorAll('[data-motion-stage]').forEach(button=>button.addEventListener('click',()=>this.setStage(Number(button.dataset.motionStage)),options));
      if(this.variant==='technology') {
        this.rows=[...this.closest('.tb-gummy-tech').querySelectorAll('.tb-gummy-tech__row')];
        this.rowObserver=new IntersectionObserver(entries=>{
          if(innerWidth<750||!this.running)return;
          const visible=entries.filter(e=>e.isIntersecting).sort((a,b)=>b.intersectionRatio-a.intersectionRatio);
          if(visible[0])this.setStage(this.rows.indexOf(visible[0].target));
        },{rootMargin:'-25% 0px -25% 0px',threshold:[0,.25,.5]});
        this.rows.forEach(row=>this.rowObserver.observe(row));
      }
    }
    ensure() {
      if(this.studio||this.failed)return;
      try {
        const style=getComputedStyle(this);
        this.studio=new Studio(this.canvas,{berry:color(style.getPropertyValue('--tb-motion-berry')),light:color(style.getPropertyValue('--tb-motion-light')),line:color(style.getPropertyValue('--tb-motion-line'))});
        this.studio.resize(this.canvas.parentElement.clientWidth,this.canvas.parentElement.clientHeight);this.render();
        this.classList.add('is-rendered');this.button.hidden=false;this.dataset.renderer='webgl';
      } catch (_) {this.failed=true;this.button.hidden=true;this.dataset.renderer='fallback';}
    }
    setStage(index) {
      this.stage=clamp(index,0,2);this.dataset.stage=String(this.stage);
      this.querySelectorAll('[data-motion-stage]').forEach(button=>button.setAttribute('aria-pressed',String(Number(button.dataset.motionStage)===this.stage)));
      this.querySelectorAll('[data-motion-caption]').forEach(caption=>{caption.hidden=Number(caption.dataset.motionCaption)!==this.stage;});
      this.render();
    }
    checkViewport() {
      const rect=this.getBoundingClientRect();
      const visible=rect.width>0&&rect.height>0&&rect.bottom>0&&rect.top<innerHeight&&rect.right>0&&rect.left<innerWidth;
      if(visible!==this.inView){this.inView=visible;this.sync();}
    }
    render() {this.studio?.render(this.time,this.variant,this.stage,this.tilt);}
    sync() {
      if(!this.isConnected)return;
      const quiet=this.motion.matches||navigator.connection?.saveData;
      const blocked=document.hidden||document.querySelector('dialog[open]');
      if(this.inView&&!blocked)this.ensure();
      const play=this.inView&&!blocked&&!this.userPaused&&(!quiet||this.userStarted)&&!!this.studio;
      if(play&&!this.running) {
        this.running=true;this.last=0;
        this.frame=requestAnimationFrame(now=>this.tick(now));
      } else if(!play) this.stop();
      this.updateButton();
    }
    tick(now) {
      if(!this.running)return;
      if(!this.last)this.last=now;
      if(now-this.last>=32) {
        this.time+=Math.min((now-this.last)/1000,.08);this.last=now;this.render();
        this.canvas.dataset.frame=String(Math.round(this.time*30));
      }
      this.frame=requestAnimationFrame(next=>this.tick(next));
    }
    stop() {this.running=false;cancelAnimationFrame(this.frame);this.updateButton();}
    updateButton() {
      this.dataset.playing=String(this.running);
      this.button.setAttribute('aria-label',this.running?this.dataset.pause:this.dataset.play);
      this.querySelector('[data-motion-label]').textContent=this.running?this.dataset.pause:this.dataset.play;
      this.querySelector('[data-motion-icon]').textContent=this.running?'Ⅱ':'▶';
    }
    disconnectedCallback() {
      this.stop();cancelAnimationFrame(this.viewportFrame);this.abort.abort();this.observer.disconnect();this.resizer.disconnect();this.rowObserver?.disconnect();this.studio?.destroy();this.studio=null;
    }
  });
})();
